/**
 * useNotificationSettings Hook
 * 
 * Manages notification settings state and API calls.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import type { 
  NotificationSettings,
  UpdateNotificationSettingsRequest,
} from '@/api/generated/client';

interface UseNotificationSettingsReturn {
  settings: NotificationSettings | null;
  loading: boolean;
  error: Error | null;
  updateSettings: (request: UpdateNotificationSettingsRequest) => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage notification settings for the current user
 */
export function useNotificationSettings(): UseNotificationSettingsReturn {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.notifications.getNotificationSettings();
      setSettings(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch notification settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSettings = useCallback(async (request: UpdateNotificationSettingsRequest) => {
    try {
      setError(null);
      const updated = await apiClient.notifications.updateNotificationSettings({
        updateNotificationSettingsRequest: request,
      });
      setSettings(updated);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update notification settings');
      setError(error);
      throw error;
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    loading,
    error,
    updateSettings,
    refetch: fetchSettings,
  };
}
